import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, ShoppingCart } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import CurrencySelector from "@/components/CurrencySelector";
import BrandMark from "@/components/BrandMark";

const navItems = [
  { name: "The Map", path: "/map", external: false },
  { name: "Archetype Test", path: "/archetype", external: false },
  { name: "About", path: "/about", external: false },
];

const SCROLLED_CLASSES = "bg-cream/85 backdrop-blur-md border-b hairline".split(" ");

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { session } = useAuth();
  const location = useLocation();
  const { count: cartCount, setCartOpen } = useCart();
  const headerRef = useRef<HTMLElement>(null);
  const isScrolledRef = useRef(false);

  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY > 30;
    if (scrolled === isScrolledRef.current) return;
    isScrolledRef.current = scrolled;
    const header = headerRef.current;
    if (!header) return;
    if (scrolled) {
      header.classList.add(...SCROLLED_CLASSES);
      header.classList.remove("bg-transparent");
    } else {
      header.classList.remove(...SCROLLED_CLASSES);
      header.classList.add("bg-transparent");
    }
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const profileLink = session ? "/profile" : "/auth";

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent"
    >
      <nav className="container mx-auto px-6 py-5">
        <div className="flex items-center justify-between gap-6">
          <Link to="/" className="transition-opacity hover:opacity-70">
            <BrandMark variant="dark" />
          </Link>

          {/* Desktop nav links */}
          <ul className="hidden lg:flex items-center gap-8 ml-auto">
            {navItems.map((item) => (
              <li key={item.path}>
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium tracking-wide text-ink/80 hover:text-ink transition-colors"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    className={cn(
                      "text-sm font-medium tracking-wide transition-colors",
                      location.pathname === item.path
                        ? "text-ink"
                        : "text-ink/70 hover:text-ink"
                    )}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Right side actions (desktop) */}
          <div className="hidden lg:flex items-center gap-2">
            <CurrencySelector />
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2.5 rounded-full text-ink/70 hover:text-ink hover:bg-ink/5 transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 w-4 h-4 bg-terracotta text-cream text-[9px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <Button
              asChild
              size="sm"
              className="rounded-full h-10 px-5 bg-ink text-cream hover:bg-ink/85 font-semibold"
            >
              <Link to={profileLink}>
                <User size={15} />
                Profile
              </Link>
            </Button>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-1">
            <CurrencySelector />
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 rounded-full text-ink/80 hover:bg-ink/5"
              aria-label="Cart"
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 w-4 h-4 bg-terracotta text-cream text-[9px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-ink"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        <div
          className={cn(
            "lg:hidden overflow-hidden transition-all duration-300",
            isOpen ? "max-h-[500px] opacity-100 mt-4" : "max-h-0 opacity-0"
          )}
        >
          <ul className="flex flex-col gap-1 py-4 bg-cream-soft rounded-2xl px-3 border hairline">
            {navItems.map((item) => (
              <li key={item.path}>
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="block px-4 py-3 rounded-xl text-ink font-medium hover:bg-ink/5"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "block px-4 py-3 rounded-xl font-medium transition-colors",
                      location.pathname === item.path
                        ? "bg-ink/10 text-ink"
                        : "text-ink/85 hover:bg-ink/5"
                    )}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
            <li className="pt-2 mt-2 border-t hairline">
              <Button
                asChild
                className="w-full rounded-full bg-ink text-cream hover:bg-ink/85"
                onClick={() => setIsOpen(false)}
              >
                <Link to={profileLink}>
                  <User size={16} className="mr-2" />
                  Profile
                </Link>
              </Button>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
